/**
 * Used to request extracts show status while uploading.
 */

// Libraries
import { useState, useEffect } from 'react'
import { useForm, useWatch, SubmitHandler, Controller } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Container, Row, Col } from 'react-bootstrap'
import { useRouteContext, useParams } from '@tanstack/react-router'
import { createPortal } from 'react-dom'
import { Toast, ToastContainer } from 'react-bootstrap'
import { LuCheck, LuX } from 'react-icons/lu'

// Scripts
import { useCoreData } from '@/queries.ts'
import { fetchAuthenticated } from '@/queries/shared'

// Components
import Card from '@/components/shared/Card'
import LoaderButton from '@/components/shared/LoaderButton'
import { CreateExtractRequest } from '@/models/extract'
import PreschoolMultiSelect from '@/components/shared/PreschoolMultiSelect'

// Component
export default function RequestODSExtractsTable({ 
    // onFileSuccess, 
    className 
}: 
{
    // onFileSuccess: (fileEntry: FileEntry) => void;
    className?: string
}) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    const { leaid } = useParams({ from: '/$leaid' })
    const coreData = useCoreData()
    const queryClient = useQueryClient()

    const [toast, setToast] = useState<{ title: string; message: string; variant: 'success' | 'error' } | null>(null)

    const programYearList = coreData?.programYearList ?? []

    const { 
        control, 
        handleSubmit, 
        register,
        reset,
        formState: { isDirty, isValid, isSubmitting }
    } = useForm<CreateExtractRequest>({
        defaultValues: {
            ExtractFileName: "",
            ExtractType: "",
            ResultsEmailDestination: "",
            IncludeHeaderRow: false,
            ProgramYear: "",
        },
    });

    useEffect(() => {
        if (programYearList.length > 0) {
            reset((formValues) => ({
                ...formValues,
                ProgramYear: formValues.ProgramYear || (programYearList[programYearList.length - 1]?.Value ?? ""),
            }));
        }
    }, [programYearList, reset]);

    const extractType = useWatch({ control, name: 'ExtractType' })
    const programYear = useWatch({ control, name: 'ProgramYear' })
    const programYearFilterExtracts = Object.keys(leaContext.extractTypeLabels).filter(x => x !== 'Preschool')
    const preschoolFilterExtracts = programYearFilterExtracts

    const uploadMutation = useMutation({
        mutationFn: async (data: CreateExtractRequest) => {
            return await fetchAuthenticated(leaContext, `/json/odata/extracts?esiidPath=${leaContext.currentUserOrg.esiIdPath}`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            })
        },
        onSuccess: () => {
            setToast({ title: "Success", message: "Export request submitted successfully.", variant: "success" })
            queryClient.invalidateQueries({ queryKey: ["extracts", leaid] })
        },
        onError: () => {
            setToast({ title: "Error", message: "Failed to submit export request. Please try again.", variant: "error" })
        }
    });

    const onSubmit: SubmitHandler<CreateExtractRequest> = async (data: CreateExtractRequest) => {
        uploadMutation.mutate(data);
    }

    const toastPrefix = toast?.variant === 'success' ? 'toastSuccess' : 'toastError'

    return (<>
        <Card className={className}>
            <div className="fs-5 fw-bold mb-3">Request Export</div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Container className="ps-3 pe-3 request-container" style={{ borderTop: 'none', borderRadius: 0 }}>
                    <Row className="form-group row">
                        <Col lg={3} className="text-lg-end">
                            <label htmlFor="ExtractType" className="mb-0">Record Type</label>
                        </Col>
                        <Col lg={7}>
                            <select
                                id="ExtractType"
                                className="form-select"
                                defaultValue=""
                                disabled={isSubmitting}
                                {...register(`ExtractType`, { required: "Extract type is required" })}
                            >
                                <option value="">Select option</option>
                                {Object.entries(leaContext.extractTypeLabels).map(([type, label]) =>
                                    <option key={type} value={type}>{label}</option>
                                )}
                            </select>
                        </Col>
                    </Row>
                    <Row className="form-group row">
                        <Col lg={3} className="text-lg-end">
                            <label htmlFor="ExtractFileName" className="mb-0">Filename</label>
                        </Col>
                        <Col lg={7}>
                            <input id="ExtractFileName" className="form-control" type="text" maxLength={50} {...register('ExtractFileName')} />
                        </Col>
                    </Row>
                    {programYearFilterExtracts.includes(extractType) &&
                        <Row className="form-group row">
                            <Col lg={3} className="text-lg-end">
                                <label htmlFor="ProgramYear" className="mb-0">Program Year</label>
                            </Col>
                            <Col lg={7}>
                                <select id={"ProgramYear"} className="form-select" {...register("ProgramYear", { required: "Program Year is required" })}>
                                    {programYearList.map((x) => <option value={x.Value} key={x.Value}>{x.Text}</option>)}
                                </select>
                            </Col>
                        </Row>
                    }
                    {preschoolFilterExtracts.includes(extractType) &&
                        <Row className="form-group row">
                            <Col lg={3} className="text-lg-end">
                                <label id="PreschoolsLabel" className="mb-0">Preschools</label>
                            </Col>
                            <Col lg={7}>
                                <Controller
                                    control={control}
                                    name="Preschools"
                                    render={({ field }) => (
                                        <PreschoolMultiSelect
                                            options={leaContext.currentOrgPreschoolsByProgramYear(programYear)}
                                            value={field.value ?? []}
                                            onChange={field.onChange}
                                            disabled={isSubmitting}
                                            ariaLabelledBy="PreschoolsLabel"
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                    }
                    <Row className="form-group row">
                        <Col lg={3} className="text-lg-end">
                            <label htmlFor="ResultsEmailDestination" className="mb-0">(Optional) Email Address to Notify When Extract Is Ready</label>
                        </Col>
                        <Col lg={7}>
                            <input id="ResultsEmailDestination" className="form-control" type="text" maxLength={50} {...register('ResultsEmailDestination')} />
                        </Col>
                    </Row>
                    <Row className="form-group row">
                        <Col lg={3} className="text-lg-end">
                        </Col>
                        <Col lg={7} className="d-flex align-items-center">
                            <label htmlFor="IncludeHeaderRow" className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                                <input
                                    id="IncludeHeaderRow"
                                    type="checkbox"
                                    className="form-check-input"
                                    disabled={isSubmitting}
                                    style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer', accentColor: '#0d2d4a', borderColor: '#495057' }}
                                    {...register("IncludeHeaderRow")}
                                />
                                Include Header Row
                            </label>
                        </Col>
                    </Row>
                </Container>
                <div className="text-right">
                    <LoaderButton
                        variant="primary"
                        type="submit"
                        disabled={!(isDirty && isValid) || isSubmitting}
                        loading={isSubmitting}
                        loadingText="Submitting..."
                    >Request Export</LoaderButton>
                </div>
            </form>
        </Card>

        {toast && createPortal(
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 2000 }}>
                <Toast
                    show
                    onClose={() => setToast(null)}
                    delay={3000}
                    autohide
                    className={`${toastPrefix}Card`}
                >
                    <div className={`${toastPrefix}Inner`}>
                        <div className={`${toastPrefix}Strip`} />
                        <div className={`${toastPrefix}Main`}>
                            <div className={`${toastPrefix}Icon`}>
                                {toast.variant === 'success' ? <LuCheck size={20} /> : <LuX size={20} />}
                            </div>
                            <div className={`${toastPrefix}Text`}>
                                <div className={`${toastPrefix}Title`}>{toast.title}</div>
                                <div className={`${toastPrefix}Message`}>{toast.message}</div>
                            </div>
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                aria-label="Close"
                                onClick={() => setToast(null)}
                            />
                        </div>
                    </div>
                </Toast>
            </ToastContainer>,
            document.body
        )}
    </>)
}